import { useToast } from '@cad-arc';
import { WorkspacePanelDto } from '@mark43/cad-api';
import { snakeCaseToLowerCase, snakeCaseToTitleCase } from '../common/helpers/utils';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { usePanelArea } from './panel-area';
import { enabledFeatureForViewByPanelType } from './panel-configuration';
import { getNewPanelPosition } from './panel-positioning-helpers';
import { useWorkspaceStore } from './use-workspace-store';
import { WorkspacePanel } from './workspace-panel';
import { ClientWorkspacePanel, EventWorkspacePanelDexFormPrefill } from './workspace-panel-types';
import { WorkspaceStore } from './workspace-store';
import { getWorkspacePanelSize } from './get-workspace-panel-size';
import { useGetOpenPanelLimit } from './use-get-open-panel-limit';
import { WorkspacePanelConfig } from './types';
import { shouldBringExistingPanelToFront } from './helpers/should-bring-existing-panel-to-front';

export type AddPanelArgs = Parameters<WorkspaceStore['addPanel']>[0];
export type OnAddPanel = (args: AddPanelArgs) => AddPanelArgs;

export type UseOpenWorkspacePanelWorkspaceConfig<
    T extends WorkspacePanelDto['workspacePanelType']
> = Omit<Partial<ClientWorkspacePanel<T>>, 'workspacePanelType'> & {
    workspacePanelType: T;
};

export function useOpenWorkspacePanel(): <
    T extends WorkspacePanelDto['workspacePanelType']
>(props: {
    onAddPanel?: OnAddPanel;
    workspacePanelConfig: UseOpenWorkspacePanelWorkspaceConfig<T>;
    matchExistingPanel?: (workspacePanel: WorkspacePanel) => boolean;
    shouldFocus?: boolean;
    dexFormPrefill?: EventWorkspacePanelDexFormPrefill;
}) => void {
    const workspacePanelsStore = useWorkspaceStore();
    const toast = useToast();
    const enabledFeatures = useEnabledFeatures();
    const panelArea = usePanelArea();
    const getOpenPanelLimit = useGetOpenPanelLimit();
    const { dexNextEnabled } = useEnabledFeatures();

    return (props: {
        onAddPanel?: OnAddPanel;
        workspacePanelConfig: WorkspacePanelConfig;
        matchExistingPanel?: (workspacePanel: WorkspacePanel) => boolean;
        shouldFocus?: boolean;
    }) => {
        const featureForPanelType =
            enabledFeatureForViewByPanelType[props.workspacePanelConfig.workspacePanelType];
        if (featureForPanelType && !enabledFeatures[featureForPanelType]) {
            toast({
                status: 'error',
                description: `Could not open ${snakeCaseToTitleCase(
                    props.workspacePanelConfig.workspacePanelType
                )} panel because feature is not enabled.`,
            });
            return;
        }
        const defaultPanelMatcher = (workspacePanel: WorkspacePanel): boolean =>
            workspacePanel.workspacePanelType === props.workspacePanelConfig.workspacePanelType;
        const panelMatcher = props.matchExistingPanel ?? defaultPanelMatcher;
        const existingPanel = workspacePanelsStore.getActivePanels().find(panelMatcher);

        if (
            shouldBringExistingPanelToFront({
                dexNextEnabled,
                workspacePanelConfig: props.workspacePanelConfig,
                existingPanel,
            })
        ) {
            workspacePanelsStore.bringPanelToFront(existingPanel as WorkspacePanel);
        } else {
            if (
                workspacePanelsStore.isPanelOpenLimitReached(
                    props.workspacePanelConfig.workspacePanelType
                )
            ) {
                const panelType = snakeCaseToLowerCase(
                    props.workspacePanelConfig.workspacePanelType
                );
                const panelOpenLimit = getOpenPanelLimit(
                    props.workspacePanelConfig.workspacePanelType
                );
                toast({
                    status: 'error',
                    description: `Unable to open ${panelType} panel.\nThere are ${panelOpenLimit} or more ${panelType} panels open. Close opened ${panelType}s to view new ${panelType}s.`,
                });
                return;
            }

            const { width, height } = getWorkspacePanelSize(
                props.workspacePanelConfig.workspacePanelType,
                enabledFeatures.eventPanelCompactByDefault
            );

            const { xcoordinate, ycoordinate, heightOverride } = getNewPanelPosition(
                workspacePanelsStore.activePanels,
                panelArea.current,
                { width, height },
                props.workspacePanelConfig.workspacePanelType
            );

            const activeWorkspaceWindowId = workspacePanelsStore.getActiveWorkspaceWindowId();

            if (!activeWorkspaceWindowId) {
                throw new Error(
                    'Cannot create workspace panel without an active workspace window id'
                );
            }

            const defaultAddPanelArgs = {
                width,
                height: heightOverride ?? height,
                xcoordinate,
                ycoordinate,
                zindex: undefined,
                windowId: activeWorkspaceWindowId,
                ...props.workspacePanelConfig,
            } as const;

            if (props.onAddPanel) {
                workspacePanelsStore.addPanel(
                    props.onAddPanel(defaultAddPanelArgs),
                    typeof props.shouldFocus !== 'undefined' ? props.shouldFocus : true
                );
            } else {
                workspacePanelsStore.addPanel(
                    defaultAddPanelArgs,
                    typeof props.shouldFocus !== 'undefined' ? props.shouldFocus : true
                );
            }
        }
    };
}
